/**
 * StockOrderDialog — ABS Stock (warehouse restock) purchase order.
 * No customer/job attached. Creates STK-YYYY-## in purchase_orders;
 * shows in Purchasing → Receiving where check-ins bump On Hand.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Warehouse, Plus, X, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getMasterParts, createStockPO } from "@/lib/api";

const emptyLine = () => ({ part_id: null, part_number: "", description: "", qty: 1, unit_cost: 0 });

function PartPicker({ onPick, testId }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await getMasterParts({ search: q.trim() });
        setResults((data || []).slice(0, 8));
        setOpen(true);
      } catch { /* silent */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="relative">
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Search catalog…"
          className="h-8 text-xs pl-7"
          data-testid={testId}
        />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-80 max-h-56 overflow-auto bg-white border border-slate-200 rounded-md shadow-lg">
          {results.map(p => (
            <button
              key={p.id}
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
              onMouseDown={(e) => { e.preventDefault(); onPick(p); setQ(""); setOpen(false); }}
              data-testid={`stock-part-result-${p.id}`}
            >
              <div className="text-xs font-medium text-slate-800 truncate">{p.name}</div>
              <div className="text-[11px] text-slate-500 flex gap-2">
                {p.part_number && <span className="font-mono">{p.part_number}</span>}
                <span>Cost ${(p.part_cost ?? 0).toFixed(2)}</span>
                <span>On Hand {p.on_hand ?? 0}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function StockOrderDialog({ open, onOpenChange, onCreated }) {
  const [vendor, setVendor] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [saving, setSaving] = useState(false);

  const reset = () => { setVendor(""); setNotes(""); setLines([emptyLine()]); };

  const setLine = (idx, patch) =>
    setLines(ls => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  const pickPart = (idx, p) =>
    setLine(idx, {
      part_id: p.id,
      part_number: p.part_number || "",
      description: p.name || "",
      unit_cost: p.part_cost ?? 0,
    });

  const total = lines.reduce((s, l) => s + (parseFloat(l.qty) || 0) * (parseFloat(l.unit_cost) || 0), 0);

  const submit = async () => {
    if (!vendor.trim()) { toast.error("Vendor is required"); return; }
    const valid = lines.filter(l => (l.part_number || l.description) && parseFloat(l.qty) > 0);
    if (!valid.length) { toast.error("Add at least one line with a part and quantity"); return; }
    setSaving(true);
    try {
      const { data } = await createStockPO({
        vendor: vendor.trim(),
        notes,
        line_items: valid.map(l => ({
          part_id: l.part_id,
          part_number: l.part_number,
          description: l.description,
          qty: parseFloat(l.qty),
          unit_cost: parseFloat(l.unit_cost) || 0,
        })),
      });
      toast.success(`${data.po_number} created — check it in under Purchasing → Receiving to bump On Hand`);
      reset();
      onOpenChange(false);
      onCreated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create stock PO");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="stock-order-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Warehouse className="w-5 h-5 text-amber-600" /> ABS Stock Order
          </DialogTitle>
          <DialogDescription>
            Warehouse restock PO (STK-#) — no customer or job. Receiving check-ins automatically increment On Hand.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Vendor</Label>
            <Input value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="e.g. Draper, Porter, Jaypro…" data-testid="stock-vendor-input" />
          </div>

          <div className="space-y-1.5">
            <div className="grid grid-cols-[1fr_90px_100px_100px_28px] gap-2 text-[11px] uppercase tracking-wide text-slate-400 px-0.5">
              <span>Part</span><span className="text-right">Qty</span><span className="text-right">Unit Cost</span><span className="text-right">Total</span><span />
            </div>
            {lines.map((l, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_90px_100px_100px_28px] gap-2 items-start" data-testid={`stock-line-${idx}`}>
                <div className="space-y-1">
                  {l.part_number || l.description ? (
                    <div className="flex items-center gap-2 h-8 px-2 rounded-md border border-slate-200 bg-slate-50 text-xs">
                      <span className="font-mono text-slate-500 shrink-0">{l.part_number}</span>
                      <span className="truncate text-slate-800">{l.description}</span>
                      <button type="button" className="ml-auto text-slate-400 hover:text-slate-600" onClick={() => setLine(idx, { part_id: null, part_number: "", description: "" })} data-testid={`stock-line-clear-${idx}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <PartPicker onPick={(p) => pickPart(idx, p)} testId={`stock-part-search-${idx}`} />
                  )}
                </div>
                <Input type="number" min="0" step="any" value={l.qty} onChange={(e) => setLine(idx, { qty: e.target.value })} className="h-8 text-xs text-right" data-testid={`stock-qty-${idx}`} />
                <Input type="number" min="0" step="0.01" value={l.unit_cost} onChange={(e) => setLine(idx, { unit_cost: e.target.value })} className="h-8 text-xs text-right" data-testid={`stock-cost-${idx}`} />
                <div className="h-8 flex items-center justify-end text-xs tabular-nums text-slate-600">
                  ${(((parseFloat(l.qty) || 0) * (parseFloat(l.unit_cost) || 0))).toFixed(2)}
                </div>
                <button type="button" className="h-8 flex items-center justify-center text-slate-400 hover:text-red-500" onClick={() => setLines(ls => ls.length > 1 ? ls.filter((_, i) => i !== idx) : ls)} data-testid={`stock-line-remove-${idx}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <div className="flex items-center justify-between pt-1">
              <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => setLines(ls => [...ls, emptyLine()])} data-testid="stock-add-line-btn">
                <Plus className="w-3 h-3 mr-1" /> Add Line
              </Button>
              <div className="text-sm font-semibold text-slate-800 tabular-nums" data-testid="stock-total">Total ${total.toFixed(2)}</div>
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="text-sm min-h-[50px]" data-testid="stock-notes-input" />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="stock-submit-btn">
            {saving ? "Creating…" : "Create Stock PO"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
